import React, { useState, useEffect } from "react";
import { MdClose, MdGavel, MdAddAlert, MdHistory, MdInfoOutline } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import MasterSideBar from "../../components/MasterSideBar"; // Unified Sidebar
import Topbar from "../../components/TopbarNav";
import ReportCaseForm from "../../components/ReportCaseForm";
import MyCases from "../../components/MyCases";
import { motion, AnimatePresence } from "framer-motion";

const FieldDisciplinaryPage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userId, setUserId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.id || decoded.userId);
      } catch (err) {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setUserId(parsedUser.id || parsedUser.userId);
        }
      }
    }
  }, []);

  if (!userId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Validating Credentials...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <MasterSideBar
        visible={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
        onLogout={() => { localStorage.clear(); window.location.href = "/"; }}
      />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <Topbar onSidebarToggle={() => setSidebarOpen(true)} onSearchChange={() => {}} />

        <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
          <div className="max-w-6xl mx-auto">
            
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-black text-gray-800 tracking-tight uppercase">
                  Disciplinary <span className="text-orange-600">Records</span>
                </h1>
                <p className="text-gray-500 text-sm">Transparency and accountability management.</p>
              </div>
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest shadow-lg hover:bg-orange-600 transition-all"
              >
                <MdAddAlert size={18} />
                Report Incident
              </button>
            </div>

            {/* Quick Context Card */}
            <div className="bg-orange-50 border border-orange-100 p-4 rounded-2xl mb-8 flex items-start gap-4">
              <div className="p-2 bg-orange-100 text-orange-600 rounded-lg">
                <MdInfoOutline size={20} />
              </div>
              <div>
                <h4 className="text-[10px] font-black text-orange-800 uppercase tracking-widest mb-1">confidentiality notice</h4>
                <p className="text-xs text-orange-700 leading-relaxed">
                  All disciplinary cases and reports are handled with the strictest confidentiality. Ensure all reports provide factual evidence and clear justifications.
                </p>
              </div>
            </div>

            {/* Cases Container */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-50 flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center text-gray-400">
                  <MdHistory size={20} />
                </div>
                <h3 className="text-xs font-black text-gray-800 uppercase tracking-widest">Case History & Filings</h3>
              </div>
              
              <div className="p-2">
                 <MyCases userId={userId} />
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Modern Centered Modal for Reporting */}
      <AnimatePresence>
        {showForm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowForm(false)}
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white w-full max-w-xl rounded-[2.5rem] shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh]"
            >
              <div className="p-8 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
                <div className="flex items-center gap-3">
                   <div className="w-10 h-10 bg-orange-600 text-white rounded-2xl flex items-center justify-center">
                      <MdGavel size={24} />
                   </div>
                   <h2 className="text-sm font-black text-gray-800 uppercase tracking-widest">
                     Initiate Incident Report
                   </h2>
                </div>
                <button
                  onClick={() => setShowForm(false)}
                  className="p-2 text-gray-400 hover:bg-white hover:text-gray-600 rounded-xl transition-all"
                >
                  <MdClose size={24} />
                </button>
              </div>

              <div className="p-8 overflow-y-auto">
                <ReportCaseForm
                  reporterId={userId}
                  onClose={() => {
                    setShowForm(false);
                    toast.success("Case reported successfully.", {
                      className: 'staff-toast-custom', // Targeted styling if needed
                    });
                  }}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ToastContainer 
        position="bottom-right"
        autoClose={4000}
        hideProgressBar
        theme="colored"
        toastStyle={{ backgroundColor: "#111827", color: "white", borderRadius: "16px" }}
      />
    </div>
  );
};

export default FieldDisciplinaryPage;